import { createFeatureSelector, createSelector } from '@ngrx/store';
import { HashMap } from '@zerops/zef/core';
import { ServiceStackTypeVersion, ServiceStackAutoscalingConfigItem, ServiceStackTypeCategories, getHAContainers } from '@zerops/models/service-stack';
// explicit import because of circular dependency
import {
  FORBIDDEN_STACK_IDS,
  NOT_IMPORTANT_STACK_IDS,
  FORBIDDEN_STACK_CATEGORIES,
  BUILD_STACK_CATEGORY,
  SERVICE_STACK_PRIORITY_MAP,
  ServiceStackCategories,
  ServiceStackTextCategories,
} from '@zerops/zerops/core/service-stack-base/service-stack-base.constant';
import { Country, Currency, ServiceStackTypeVersionItem } from '@zerops/models/settings';
import { SettingsBaseState } from './settings-base.model';
import { FEATURE_NAME } from './settings-base.constant';

export const selectSettingsBaseState = createFeatureSelector<SettingsBaseState>(FEATURE_NAME);

export const selectLanguages = createSelector(
  selectSettingsBaseState,
  (state) => state.languageList
);

export const selectCountries = createSelector(
  selectSettingsBaseState,
  (state) => state.countryList
);

export const selectCountryMap = createSelector(
  selectCountries,
  (countryList) => countryList?.reduce((obj, itm) => {
    obj[itm.id] = itm;
    return obj;
  }, {} as HashMap<Country>)
);

export const selectCurrencies = createSelector(
  selectSettingsBaseState,
  (state) => state.currencyList
);

export const selectServiceStackTypes = createSelector(
  selectSettingsBaseState,
  (state) => state.serviceStackList.filter((d) => !FORBIDDEN_STACK_IDS.includes(d.id)
    && !FORBIDDEN_STACK_CATEGORIES.includes(d.category)
  )
);

export const selectImportantServiceStackTypes = createSelector(
  selectSettingsBaseState,
  (state) => state.serviceStackList.filter((d) => !FORBIDDEN_STACK_IDS.includes(d.id)
    && !FORBIDDEN_STACK_CATEGORIES.includes(d.category)
    && !NOT_IMPORTANT_STACK_IDS.includes(d.id as any)
  )
);

export const selectBuildServiceStackTypes = createSelector(
  selectSettingsBaseState,
  (state) => state.serviceStackList.filter((d) => d.category === BUILD_STACK_CATEGORY)
);

export const selectStackTypeDetailMap = createSelector(
  selectSettingsBaseState,
  (state) => state.stackTypeDetailMap
);

export const selectStackTypeDetail = (id: string) => createSelector(
  selectStackTypeDetailMap,
  (detailMap) => detailMap[id]
);

export const selectServiceStackTypesVersionsMap = createSelector(
  selectSettingsBaseState,
  (state) => {

    const dataMap = state.serviceStackList.reduce((obj, itm) => {
      // Use detail data if available (has full version config), otherwise use light data
      const stackType = state.stackTypeDetailMap[itm.id] || itm;
      obj.map[stackType.id] = stackType;

      obj.data = [
        ...obj.data,
        ...stackType.serviceStackTypeVersionList
      ];
      return obj;
    }, {
      map: {},
      data: [] as ServiceStackTypeVersion[]
    });

    return dataMap.data.reduce((obj, serviceStackTypeVersion) => {
      const serviceStackType = dataMap.map[serviceStackTypeVersion.serviceStackTypeId];
      const serviceStackTypeName = serviceStackType.name;
      const serviceStackTypeVersionName = serviceStackTypeVersion.versionNumber;

      obj[serviceStackTypeVersion.id] = {
        name: `${serviceStackTypeName} ${serviceStackTypeVersionName}`,
        serviceStackTypeName,
        serviceStackTypeVersionName,
        serviceStackType,
        serviceStackTypeVersion
      };

      return obj;
    }, {}) as HashMap<ServiceStackTypeVersionItem>;

  }
);

export const selectCurrencyMap = createSelector(
  selectCurrencies,
  (currencies) => currencies.reduce((obj, curr) => {
    obj[curr.id] = curr;
    return obj;
  }, {}) as HashMap<Currency>
);

export const selectCurrencyById = (id: string) => createSelector(
  selectCurrencyMap,
  (currencyMap) => currencyMap[id]
);

export const selectActiveCurrency = createSelector(
  selectSettingsBaseState,
  (state) => state.activeCurrency
);

export const selectLocations = createSelector(
  selectSettingsBaseState,
  (state) => state.locationList || []
);

export const selectLocationById = (id: string) => createSelector(
  selectLocations,
  (locations) => locations.find((location) => location.id === id)
);

export const selectDefaultLocation = createSelector(
  selectLocations,
  (locations) => locations?.length ? locations[0] : null
);

export const selectLocationPingMap = createSelector(
  selectSettingsBaseState,
  (state) => state.locationPingMap || {}
);

export const selectFastestLocationId = createSelector(
  selectSettingsBaseState,
  (state) => state.fastestLocationId
);

export const selectFastestLocation = createSelector(
  selectLocations,
  selectFastestLocationId,
  (locations, fastestLocationId) => {
    // Wait for ping measurements to complete - don't return fallback
    if (!fastestLocationId) {
      return null;
    }

    if (!locations || locations.length === 0) {
      return null;
    }

    return locations.find((location) => location.id === fastestLocationId) || locations[0];
  }
);

// # Service stack type map (id -> ServiceStackType)
export const selectServiceStackTypeMap = createSelector(
  selectSettingsBaseState,
  (state) => state.serviceStackList.reduce((map, stack) => {
    map[stack.id] = stack;
    return map;
  }, {} as HashMap<any>)
);

// # Feature flag sets derived from API
export const selectServicesWithAccessDetail = createSelector(
  selectSettingsBaseState,
  (state) => new Set(state.serviceStackList.filter((s) => s.hasAccessDetails).map((s) => s.id))
);

export const selectServicesWithBackup = createSelector(
  selectSettingsBaseState,
  (state) => new Set(state.serviceStackList.filter((s) => s.hasBackup).map((s) => s.id))
);

export const selectServicesWithConfiguration = createSelector(
  selectSettingsBaseState,
  (state) => new Set(state.serviceStackList.filter((s) => s.hasConfiguration).map((s) => s.id))
);

// # Category maps derived from API
export const selectServiceStackCategoryTextMap = createSelector(
  selectSettingsBaseState,
  (state) => state.serviceStackList.reduce((map, stack) => {
    map[stack.id] = stack.subcategory;
    return map;
  }, {} as Record<string, string>)
);

const SUBCATEGORY_TO_CATEGORY: Record<string, string> = {
  [ServiceStackTextCategories.Database]: ServiceStackCategories.Managed,
  [ServiceStackTextCategories.Messaging]: ServiceStackCategories.Managed,
  [ServiceStackTextCategories.SearchEngine]: ServiceStackCategories.Managed,
  [ServiceStackTextCategories.RuntimeEnvironment]: ServiceStackCategories.Runtime,
  [ServiceStackTextCategories.PhpWebserver]: ServiceStackCategories.Runtime,
  [ServiceStackTextCategories.Webserver]: ServiceStackCategories.Static,
  [ServiceStackTextCategories.Static]: ServiceStackCategories.Static,
  [ServiceStackTextCategories.LinuxContainer]: ServiceStackCategories.Bare,
  [ServiceStackTextCategories.VirtualMachine]: ServiceStackCategories.Bare,
  [ServiceStackTextCategories.Storage]: ServiceStackCategories.Storage,
  [ServiceStackTextCategories.Zcp]: ServiceStackCategories.Bare,
};

export const selectServiceStackCategoryMap = createSelector(
  selectSettingsBaseState,
  (state) => state.serviceStackList.reduce((map, stack) => {
    if (stack.category === ServiceStackTypeCategories.Standard) {
      // Managed storages (seaweedfs) are STANDARD with the storage subcategory and belong to the storages tile group.
      // Everything else STANDARD stays managed, regardless of what the table says about its subcategory.
      map[stack.id] = SUBCATEGORY_TO_CATEGORY[stack.subcategory] === ServiceStackCategories.Storage
        ? ServiceStackCategories.Storage
        : ServiceStackCategories.Managed;
    } else if (
      stack.category === ServiceStackTypeCategories.ObjectStorage
      || stack.category === ServiceStackTypeCategories.LocalStorage
    ) {
      map[stack.id] = ServiceStackCategories.Storage;
    } else {
      map[stack.id] = SUBCATEGORY_TO_CATEGORY[stack.subcategory] || ServiceStackCategories.Runtime;
    }
    return map;
  }, {} as Record<string, string>)
);

// # Autoscaling config derived from API

export const selectServiceStackAutoscalingConfigMap = createSelector(
  selectSettingsBaseState,
  (state) => state.serviceStackList.reduce((map, stack) => {
    const isUser = stack.category === ServiceStackTypeCategories.User;
    const isObjectStorage = stack.category === ServiceStackTypeCategories.ObjectStorage;
    const haContainers = getHAContainers(stack.id, stack.modeList);

    map[stack.id] = {
      horizontal: isUser,
      vertical: !isObjectStorage,
      mode: stack.modeList?.length > 1,
      ...(haContainers ? { HAContainers: haContainers } : {})
    };

    return map;
  }, {} as Record<string, ServiceStackAutoscalingConfigItem>)
);

// # Docs URL map derived from API
export const selectServiceStackDocsUrlMap = createSelector(
  selectSettingsBaseState,
  (state) => state.serviceStackList.reduce((map, stack) => {
    if (stack.docsUrl) {
      map[stack.id] = stack.docsUrl;
    }
    return map;
  }, {} as Record<string, string>)
);

// # Priority-sorted service stack types
export const selectSortedServiceStackTypes = createSelector(
  selectServiceStackTypes,
  (types) => [...types].sort((a, b) => {
    const priorityA = SERVICE_STACK_PRIORITY_MAP[a.id] ?? 999;
    const priorityB = SERVICE_STACK_PRIORITY_MAP[b.id] ?? 999;
    return priorityA - priorityB;
  })
);
